/**
 * Series palettes, mirroring the raw `--xa-*` Xait Design System color tokens.
 * Charts need resolvable hex values for SVG, so these are concrete.
 */
import type { XaPalette } from './types.js';

/** Raw Xait brand colors used by the charts (subset of the DS token sheet). */
export const XA_COLORS = {
  grape: '#553c83',
  violets: '#9e76fd',
  mauve: '#cbb5ff',
  teal: '#008883',
  aqua: '#82cac9',
  sapphire: '#0d49bf',
  saffron: '#f88f33',
  fuzzy: '#cf7275',
  pastelBlue: '#83a7c9',
  fawn: '#dda875',
  lavender: '#bf849b',
  glossy: '#a18fb9',
  straw: '#ebde76',
  aquamarine: '#6effd1',
  pink: '#ffb2da',
  greenSheen: '#81b1a5',
  steelSilver: '#b8c2c9',
} as const;

/** Cool violet-cast neutral ramp (`--xa-ink-*`) — chart chrome. */
export const XA_INK = {
  ink50: '#f7f6fa',
  ink100: '#f0eff4',
  ink200: '#dcdae3',
  ink300: '#bcb9c6',
  ink400: '#928fa1',
  ink500: '#6f6b80',
  ink800: '#2c2a38',
  ink950: '#15141c',
} as const;

/** Chart chrome roles, resolved to ink-ramp hexes. */
export const XA_CHROME = {
  /** Regular gridlines + horizontal-bar tracks. */
  grid: XA_INK.ink100,
  /** Y-axis baseline (value 0). */
  baseline: XA_INK.ink200,
  /** Crosshair + pie leader lines. */
  crosshair: XA_INK.ink300,
  /** Axis tick text. */
  axisText: XA_INK.ink400,
  /** Tooltip background / headline values. */
  tooltipBg: XA_INK.ink950,
  /** Toggled-off legend swatch. */
  swatchOff: XA_INK.ink300,
} as const;

export const palettes: Record<'brand' | 'vivid' | 'mono' | 'tokens', readonly string[]> = {
  /**
   * Default categorical palette, drawn from the cool blue/teal/grey brand
   * colours that match the XaitCPQ quote document: Dark Pastel Blue leads, with
   * Teal Green, Steel Silver, Pearl Aqua, Sapphire and Green Sheen following.
   */
  brand: [
    XA_COLORS.pastelBlue, XA_COLORS.teal, XA_COLORS.steelSilver,
    XA_COLORS.aqua, XA_COLORS.sapphire, XA_COLORS.greenSheen,
  ],
  /** Same cool family, bold-led (Sapphire first) for screen dashboards. */
  vivid: [
    XA_COLORS.sapphire, XA_COLORS.teal, XA_COLORS.pastelBlue,
    XA_COLORS.aqua, XA_COLORS.steelSilver, XA_COLORS.greenSheen,
  ],
  /** Single-hue blue ramp (Sapphire → Dark Pastel Blue) for ordered / part-to-whole data (pie default). */
  mono: ['#0a3a99', XA_COLORS.sapphire, '#3f74d1', XA_COLORS.pastelBlue, '#aac3da', '#dbe6ef'],
  /** Token split: input / output / reasoning — graduated product blues. */
  tokens: [XA_COLORS.sapphire, XA_COLORS.pastelBlue, XA_COLORS.steelSilver],
};

/** Resolve a palette name or explicit hex array to a concrete color list. */
export function resolvePalette(palette: XaPalette): readonly string[] {
  if (Array.isArray(palette)) return palette;
  const p = palettes[palette as keyof typeof palettes];
  if (!p) throw new Error(`XaitCharts: unknown palette "${palette}"`);
  return p;
}

/** Convert HSL (h in degrees, s/l in 0..1) to a `#rrggbb` hex string. */
function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const [r1, g1, b1] =
    hp < 1 ? [c, x, 0] :
    hp < 2 ? [x, c, 0] :
    hp < 3 ? [0, c, x] :
    hp < 4 ? [0, x, c] :
    hp < 5 ? [x, 0, c] :
             [c, 0, x];
  const m = l - c / 2;
  const to = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r1)}${to(g1)}${to(b1)}`;
}

/**
 * Deterministic vibrant color for overflow index `k` (0-based) once the supplied
 * palette is exhausted. Hues march around the wheel by the golden angle so any
 * count of extra series stays visually well-separated; the start hue is warm
 * (orange) to pop against the cool brand palette, and saturation/lightness are
 * varied in a short cycle so neighbours that land on nearby hues still differ in
 * tone. Pure + side-effect-free, so the same index always yields the same color.
 */
export function overflowColor(k: number): string {
  const GOLDEN_ANGLE = 137.508;
  const hue = 28 + k * GOLDEN_ANGLE;
  const sat = [0.82, 0.7, 0.9][k % 3];
  const light = [0.52, 0.46, 0.6][k % 3];
  return hslToHex(hue, sat, light);
}

/**
 * Color for datum `i`: an explicit datum color wins; within the palette it is
 * `colors[i]`; once the palette is exhausted (`i >= colors.length`) a distinct
 * vibrant color is generated instead of repeating, so high-cardinality charts
 * (more series than palette entries) keep every series visually distinguishable.
 */
export function seriesColor(explicit: string | undefined, colors: readonly string[], i: number): string {
  if (explicit) return explicit;
  if (i < colors.length) return colors[i];
  return overflowColor(i - colors.length);
}
